import { type RefObject, useRef, useEffect } from 'react'

type DelayOptions = {
  callback: () => void
  once?: boolean
  threshold?: number
  delay?: number
}

// 缓存可见性检查结果，避免重复计算
const visibilityCache = new WeakMap<Element, { visible: boolean; timestamp: number }>()
const CACHE_DURATION = 500 // 缓存500ms，平衡性能和准确性

/**
 * 检查元素是否真正可见（优化版本，带缓存）
 * @param element 要检查的元素
 * @returns 元素是否可见
 */
const isElementVisible = (element: Element): boolean => {
  const now = Date.now()

  // 检查缓存
  const cached = visibilityCache.get(element)
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.visible
  }

  const el = element as HTMLElement

  // 快速检查：元素是否存在
  if (!el) {
    visibilityCache.set(element, { visible: false, timestamp: now })
    return false
  }

  // 快速检查：元素是否在DOM中
  if (!document.contains(el)) {
    visibilityCache.set(element, { visible: false, timestamp: now })
    return false
  }

  // 使用 getBoundingClientRect 进行快速检查（比 getComputedStyle 更快）
  const rect = el.getBoundingClientRect()

  // 如果元素没有尺寸，直接返回 false（这比检查 CSS 样式更快）
  if (rect.width === 0 || rect.height === 0) {
    visibilityCache.set(element, { visible: false, timestamp: now })
    return false
  }

  // 只有在必要时才检查 CSS 样式（延迟检查）
  // 先检查最常见的隐藏情况
  const style = el.style
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    visibilityCache.set(element, { visible: false, timestamp: now })
    return false
  }

  // 如果内联样式没有隐藏，再检查计算样式（更昂贵的操作）
  // 但只在元素确实有尺寸且在视口内时才检查
  if (rect.width > 0 && rect.height > 0) {
    const computedStyle = window.getComputedStyle(el)
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
      visibilityCache.set(element, { visible: false, timestamp: now })
      return false
    }
  }

  const visible = true
  visibilityCache.set(element, { visible, timestamp: now })
  return visible
}
/**
 * 只有提留超过delay设置的时间才会触发callback
 * @param targetRef
 * @param callback
 * @param once
 * @param threshold
 * @param delay
 */
export const useIntersectionObserverDelay = (
  targetRef: RefObject<any> | undefined,
  { callback, once, threshold = 0.8, delay = 0 }: DelayOptions
) => {
  const timeoutRef = useRef<number>(0)
  const triggeredRef = useRef(false)

  useEffect(() => {
    if (!targetRef?.current) {
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      return
    }

    const current = targetRef.current
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: threshold,
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isElementVisible(entry.target)) {
          if (delay) {
            window.clearTimeout(timeoutRef.current)
            if (triggeredRef.current) return
            timeoutRef.current = window.setTimeout(() => {
              callback()
              triggeredRef.current = true
            }, delay)
          } else {
            callback()
            triggeredRef.current = true
          }

          if (once && triggeredRef.current) {
            observer.disconnect()
          }
        } else {
          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current)
          }
        }
      })
    }, options)
    if (current) {
      observer.observe(current)
    }
    return () => {
      observer.disconnect()
      window.clearTimeout(timeoutRef.current)
    }
  }, [once, threshold, delay, targetRef?.current])
}
